import { routes } from "@constants/routes";
import {
  BuildingIcon,
  FingerprintScanIcon,
  JobPositionCard,
  Loading,
  OrganizationJobPositionNode,
  SearchBox,
  Typography,
  UserScanIcon,
  Work2Icon,
} from "@cpj-ai/panel-npm-package";
import {
  AdminJobPositionNode,
  AuthAccountOrganizationJobPositionStatusChoices,
  useGetOrganizationJobPositionsQuery,
} from "@graphql/generated";
import Cards from "@modules/company/components/CardsItem";
import { useNavigate, useParams } from "react-router-dom";
import { NotRegisteredJobPositionCard } from "./NotRegisteredJobPositionCard";
import { IFormData, inputConfigs } from "./searchBoxFormConfig";

const JobPositionTab = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    data: jobPositions,
    loading,
    refetch,
  } = useGetOrganizationJobPositionsQuery({
    variables: {
      organizationId: id as string,
    },
    skip: !id,
  });

  const cards = [
    {
      title: jobPositions?.admin?.organizations?.edges[0]?.node?.jobPositionsStatusCounts?.published,
      description: "Approved and published",
      icon: (
        <div className="bg-blue-400 rounded-lg p-2">
          <BuildingIcon width={24} height={24} stroke="white" />
        </div>
      ),
    },
    {
      title: jobPositions?.admin?.organizations?.edges[0]?.node?.jobPositionsStatusCounts?.submitted,
      description: "Awaiting approval",
      icon: (
        <div className="bg-cyan-600 rounded-lg p-2">
          <Work2Icon width={24} height={24} fill="white" />
        </div>
      ),
    },
    {
      title: jobPositions?.admin?.organizations?.edges[0]?.node?.jobPositionsStatusCounts?.drafted,
      description: "Awaiting certification",
      icon: (
        <div className="bg-amber-600 rounded-lg p-2">
          <FingerprintScanIcon width={24} height={24} viewBox="0 0 34 34" stroke="white" />
        </div>
      ),
    },
    {
      title: [
        jobPositions?.admin?.organizations?.edges[0]?.node?.jobPositionsStatusCounts?.suspended,
        jobPositions?.admin?.organizations?.edges[0]?.node?.jobPositionsStatusCounts?.expired,
        jobPositions?.admin?.organizations?.edges[0]?.node?.jobPositionsStatusCounts?.rejected,
      ].reduce((acc, curr) => (acc ?? 0) + (curr || 0), 0),
      description: "Inactive and non-absorbing",
      icon: (
        <div className="bg-red-400 rounded-lg p-2">
          <UserScanIcon width={24} height={24} stroke="white" />
        </div>
      ),
    },
  ];

  const filterData = (data: IFormData) => {
    refetch({
      title: data.title,
      status: data.status?.value !== "all" ? data.status?.value : undefined,
      startDate: data.date?.[0],
      endDate: data.date?.at(-1),
    });
  };

  const rejectedAndSubmittedJobPositions = jobPositions?.admin?.jobPositions?.edges.filter(
    (jobPosition) =>
      jobPosition?.node?.status === AuthAccountOrganizationJobPositionStatusChoices.REJECTED ||
      jobPosition?.node?.status === AuthAccountOrganizationJobPositionStatusChoices.SUBMITTED,
  );

  const registeredJobPositions = jobPositions?.admin?.jobPositions?.edges.filter(
    (jobPosition) =>
      jobPosition?.node?.status !== AuthAccountOrganizationJobPositionStatusChoices.REJECTED &&
      jobPosition?.node?.status !== AuthAccountOrganizationJobPositionStatusChoices.SUBMITTED,
  );

  const navigateToJobPosition = (jobPositionId: string) => {
    navigate(routes.jobPositions.view.replace(":id", jobPositionId));
  };

  return (
    <div>
      <Cards className="mb-9" items={cards} />
      <SearchBox inputConfigs={inputConfigs} filterItems={filterData} />
      {jobPositions?.admin?.jobPositions?.edges.length ? (
        <div className="mt-5 flex flex-col gap-5">
          {rejectedAndSubmittedJobPositions?.length ? (
            <div>
              <Typography variant="h5" className="!font-normal mb-3.5">
                Waiting for confirmation :
              </Typography>
              <div className="flex flex-col gap-3.5">
                {rejectedAndSubmittedJobPositions?.map((jobPosition) => (
                  <NotRegisteredJobPositionCard
                    key={jobPosition?.node?.id}
                    jobPosition={jobPosition?.node as AdminJobPositionNode}
                    onPress={navigateToJobPosition}
                  />
                ))}
              </div>
            </div>
          ) : null}
          {registeredJobPositions?.length ? (
            <div>
              <Typography variant="h5" className="!font-normal mb-3.5">
                Registered :
              </Typography>
              <div className="flex flex-col gap-3.5">
                {loading ? (
                  <Loading className="pt-40" />
                ) : (
                  registeredJobPositions?.map((jobPosition) => (
                    <JobPositionCard
                      key={jobPosition?.node?.id}
                      jobPosition={jobPosition?.node as OrganizationJobPositionNode}
                      onPress={navigateToJobPosition}
                    />
                  ))
                )}
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="mt-20 flex flex-col gap-5 w-full items-center justify-center">
          <Typography variant="h5" className="!font-normal mb-3.5 text-slate-600">
            No job positions found
          </Typography>
        </div>
      )}
    </div>
  );
};

export default JobPositionTab;
